import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Collapse, Link, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import SearchableSelect from '../../common/SearchableSelect';
import { ExpandLess, ExpandMore, GppMaybeOutlined, LockOutlined, VerifiedOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import financeService from '../../../services/financeService';
import { FiscalPeriod, FiscalPeriodStatus } from '../../../types/finance';
import { useTimezone } from '../../../hooks/useTimezone';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import { useAuth } from '../../../context/AuthContext';
import { useApprovalRule } from '../../../hooks/useApprovalRule';
import { ChipTone, ToneChip } from '../../common/StatusChip';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import ReasonDialog from '../../common/ReasonDialog';
import TableRowSkeletons from '../../common/TableRowSkeletons';
import JournalEntryDrawer from '../journal/JournalEntryDrawer';
import CloseChecklistDialog from './CloseChecklistDialog';
import YearEndCard, { fyLabel } from './YearEndCard';

const STATUS: Record<FiscalPeriodStatus, { label: string; tone: ChipTone }> = {
  OPEN: { label: 'Open', tone: 'success' },
  CLOSED: { label: 'Closed', tone: 'warning' },
  LOCKED: { label: 'Locked', tone: 'neutral' },
};

const ACTION_PAST: Record<string, string> = { CLOSE: 'Closed', LOCK: 'Locked', REOPEN: 'Reopened' };

type Pending = { period: FiscalPeriod; action: 'close' | 'lock' | 'reopen' };

/**
 * The fiscal year month by month. Close a month once its checklist passes
 * (it is sealed with a checksum), lock it once final. Months close and lock
 * in order; only the latest closed month can reopen, with a reason — and a
 * second person when the school requires one. The year's last month locks
 * with the year-end close.
 */
const FiscalPeriodsPanel: React.FC = () => {
  const theme = useTheme();
  const { formatDate } = useTimezone();
  const { canWrite } = useModulePermissions();
  const { decodedToken } = useAuth();
  const { ownBlocked } = useApprovalRule();
  const { enqueueSnackbar } = useSnackbar();
  const [years, setYears] = useState<number[]>([]);
  const [fiscalYear, setFiscalYear] = useState<number | null>(null);
  const [periods, setPeriods] = useState<FiscalPeriod[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [openEntryId, setOpenEntryId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // Whether a reopen needs a second person — only shapes the wording; the server decides.
  const [twoPerson, setTwoPerson] = useState<boolean | null>(null);
  const editable = canWrite('finance_periods');

  useEffect(() => {
    financeService.getFiscalYears()
      .then(r => { setYears(r.years); setFiscalYear(r.current); })
      .catch((e: any) => setError(e?.message || 'Could not load fiscal years.'));
    financeService.getSettings()
      .then(s => setTwoPerson(s.requireJournalApproval))
      .catch(() => setTwoPerson(null));
  }, []);

  const load = useCallback(async () => {
    if (!fiscalYear) return;
    try {
      setPeriods(await financeService.getPeriods(fiscalYear));
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load fiscal periods.');
    }
  }, [fiscalYear]);

  useEffect(() => { setPeriods(null); load(); }, [load]);

  const changed = async () => { setRefreshKey(k => k + 1); await load(); };

  const lock = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await financeService.changePeriod(pending.period._id, 'lock');
      enqueueSnackbar(`${monthOf(pending.period)} locked`, { variant: 'success' });
      setPending(null);
      await changed();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'That change was refused.', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  /** Reopen, or ask to: the server records a request when a second person must approve. */
  const reopen = async (period: FiscalPeriod, reason?: string) => {
    const after = await financeService.changePeriod(period._id, 'reopen', reason);
    enqueueSnackbar(after.status === 'OPEN' ? `${monthOf(period)} reopened` : `Reopen of ${monthOf(period)} requested — someone else must approve it`, { variant: 'success' });
    await changed();
  };

  const decide = async (period: FiscalPeriod, approve: boolean) => {
    setBusy(true);
    try {
      if (approve) await reopen(period);
      else {
        await financeService.changePeriod(period._id, 'decline-reopen');
        enqueueSnackbar(`Reopen request on ${monthOf(period)} withdrawn`, { variant: 'success' });
        await changed();
      }
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'That change was refused.', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const monthOf = (p: FiscalPeriod) => {
    const [y, m] = p.key.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  };

  // Months close and lock in order and reopen from the latest, so only the
  // one month each action can apply to offers it.
  const next = React.useMemo(() => {
    const list = periods ?? [];
    const firstOpen = list.findIndex(p => p.status === 'OPEN');
    const firstUnlocked = list.findIndex(p => p.status !== 'LOCKED');
    const lastClosed = list.map(p => p.status).lastIndexOf('CLOSED');
    return {
      close: list[firstOpen]?._id,
      lock: list[firstUnlocked]?.status === 'CLOSED' ? list[firstUnlocked]._id : undefined,
      reopen: lastClosed >= 0 && list.slice(lastClosed + 1).every(p => p.status === 'OPEN') ? list[lastClosed]._id : undefined,
    };
  }, [periods]);

  const lastKey = periods?.[periods.length - 1]?.key;

  const actionFor = (p: FiscalPeriod) => {
    if (p.status === 'LOCKED') return <LockOutlined fontSize="small" sx={{ color: 'text.disabled', mr: 1 }} />;
    if (p._id === next.close) {
      const ended = new Date(p.endDate) <= new Date();
      return (
        <Tooltip title={ended ? '' : `Can close once the month ends`}>
          <span><Button size="small" disabled={!ended} onClick={() => setPending({ period: p, action: 'close' })}>Close</Button></span>
        </Tooltip>
      );
    }
    const request = p.reopenRequest;
    if (request) {
      const mine = String(request.by) === decodedToken?.userId;
      return ownBlocked(mine) ? (
        <Button size="small" color="inherit" disabled={busy} onClick={() => decide(p, false)}>Withdraw</Button>
      ) : (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Button size="small" color="inherit" disabled={busy} onClick={() => decide(p, false)}>{mine ? 'Withdraw' : 'Decline'}</Button>
          <Button size="small" color="warning" variant="outlined" disabled={busy} onClick={() => decide(p, true)}>Approve</Button>
        </Stack>
      );
    }
    return (
      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
        {p._id === next.reopen && <Button size="small" color="inherit" onClick={() => setPending({ period: p, action: 'reopen' })}>Reopen</Button>}
        {p._id === next.lock && (p.key === lastKey ? (
          <Tooltip title="The year's last month locks with the year-end close">
            <Typography variant="caption" color="text.disabled" sx={{ px: 1 }}>Locks at year end</Typography>
          </Tooltip>
        ) : <Button size="small" onClick={() => setPending({ period: p, action: 'lock' })}>Lock</Button>)}
      </Stack>
    );
  };

  /** Sealed, or changed after close — beside the status. */
  const seal = (p: FiscalPeriod) => {
    if (p.tampered) {
      return (
        <Tooltip title="The nightly check found this month's lines changed outside the ledger since it closed. Contact support.">
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'error.main' }}>
            <GppMaybeOutlined sx={{ fontSize: 18 }} />
            <Typography variant="caption" fontWeight={700}>Changed after close</Typography>
          </Stack>
        </Tooltip>
      );
    }
    if (!p.sealed) return null;
    return (
      <Tooltip title="Sealed at close: a checksum over the month's lines, verified nightly">
        <VerifiedOutlined sx={{ fontSize: 18, color: 'success.main' }} />
      </Tooltip>
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <SearchableSelect
          label="Fiscal year"
          value={fiscalYear ? String(fiscalYear) : ''}
          onChange={v => v && setFiscalYear(Number(v))}
          options={years.map(y => ({ value: String(y), label: fyLabel(y) }))}
          clearable={false}
          minWidth={200}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { py: 1.1 } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
                <TableCell>Month</TableCell>
                <TableCell sx={{ width: 110 }} align="right">Entries</TableCell>
                <TableCell sx={{ width: 120 }}>Status</TableCell>
                <TableCell>Last change</TableCell>
                {editable && <TableCell sx={{ width: 190 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {!periods ? <TableRowSkeletons columns={editable ? 5 : 4} rows={12} rowHeight={40} /> : periods.map(p => {
                const last = p.history[p.history.length - 1];
                const elevated = p.elevatedPostings.length;
                const open = expanded === p._id;
                return (
                  <React.Fragment key={p._id}>
                  <TableRow hover sx={{ ...(p.tampered && { bgcolor: alpha(theme.palette.error.main, 0.04) }), ...(open && { '& td': { borderBottom: 0 } }) }}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography variant="body2" fontWeight={500}>{monthOf(p)}</Typography></TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color={p.entries ? 'text.primary' : 'text.disabled'} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {p.entries || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ToneChip size="small" tone={STATUS[p.status].tone} label={STATUS[p.status].label} />
                        {seal(p)}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {p.reopenRequest ? (
                        <Typography variant="caption" color="warning.dark" fontWeight={600} sx={{ display: 'block' }}>
                          Reopen requested by {p.reopenRequest.byName ?? 'Unknown'} — {p.reopenRequest.reason}
                          {ownBlocked(String(p.reopenRequest.by) === decodedToken?.userId) && <Box component="span" sx={{ fontWeight: 400 }}> · waiting for someone else</Box>}
                        </Typography>
                      ) : last ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {ACTION_PAST[last.action]} by {last.byName ?? 'Unknown'}
                          {last.approvedByName && <>, approved by {last.approvedByName}</>} · {formatDate(last.at)}
                          {last.reason && <> — {last.reason}</>}
                        </Typography>
                      ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                      {elevated > 0 && (
                        <Link component="button" variant="caption" underline="hover" onClick={() => setExpanded(open ? null : p._id)}
                          sx={{ display: 'inline-flex', alignItems: 'center', fontWeight: 600 }}>
                          {elevated} posted after close
                          {open ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                        </Link>
                      )}
                    </TableCell>
                    {editable && <TableCell align="right">{actionFor(p)}</TableCell>}
                  </TableRow>
                  {elevated > 0 && (
                    <TableRow>
                      <TableCell colSpan={editable ? 5 : 4} sx={{ py: '0 !important', ...(!open && { borderBottom: 0 }) }}>
                        <Collapse in={open} unmountOnExit>
                          <Box sx={{ pl: 2, pb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              Entries posted into this month after it closed, by someone who manages fiscal periods. The seal was renewed each time.
                            </Typography>
                            {p.elevatedPostings.map(e => (
                              <Stack key={e.entryId} direction="row" spacing={1.5} alignItems="baseline" sx={{ py: 0.25 }}>
                                <Link component="button" variant="body2" underline="hover" onClick={() => setOpenEntryId(e.entryId)}
                                  sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                                  {e.entryNo}
                                </Link>
                                <Typography variant="caption" color="text.secondary">{e.byName ?? 'Unknown'} · {formatDate(e.at)}</Typography>
                              </Stack>
                            ))}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {fiscalYear && <YearEndCard fiscalYear={fiscalYear} refreshKey={refreshKey} editable={editable} onClosed={changed} />}

      <CloseChecklistDialog
        periodId={pending?.action === 'close' ? pending.period._id : null}
        monthLabel={pending ? monthOf(pending.period) : ''}
        onClose={() => setPending(null)}
        onClosed={async () => {
          enqueueSnackbar(`${monthOf(pending!.period)} closed and sealed`, { variant: 'success' });
          setPending(null);
          await changed();
        }}
      />
      <ConfirmationDialog
        open={pending?.action === 'lock'}
        onClose={() => setPending(null)}
        onConfirm={lock}
        loading={busy}
        title={pending ? `Lock ${monthOf(pending.period)}?` : ''}
        message="A locked month is final: no one can post into it or reopen it. Lock only once its books are reviewed."
        confirmText="Lock month"
        confirmColor="error"
      />
      <ReasonDialog
        open={pending?.action === 'reopen'}
        onClose={() => setPending(null)}
        title={pending ? `${twoPerson ? 'Ask to reopen' : 'Reopen'} ${monthOf(pending.period)}?` : ''}
        consequence={twoPerson
          ? 'Someone else who manages fiscal periods must approve before the month opens. Once open, anyone who can record journals can post into it again.'
          : 'Anyone who can record journals will be able to post into it again. The reason is kept with the month.'}
        confirmText={twoPerson ? 'Request reopen' : 'Reopen'}
        confirmColor="warning"
        reasonPlaceholder="Why does this month need to reopen?"
        onConfirm={async reason => { await reopen(pending!.period, reason); setPending(null); }}
      />
      <JournalEntryDrawer entryId={openEntryId} onClose={() => setOpenEntryId(null)} onChanged={changed} />
    </Box>
  );
};

export default FiscalPeriodsPanel;
